import React, { useState } from 'react';
import { FiSearch, FiChevronUp, FiChevronDown, FiUsers } from 'react-icons/fi';
import { MdOutlineStorefront } from 'react-icons/md';
import methodModel from '../../../methods/methods';
import VenueVisitorsPanel, { StackedAvatarGroup } from './VenueVisitorsPanel';

/* ── Column widths ── */
const COL = {
  // venue: '26%',
  venue: '21%',
  visitors: '18%',
  users: '14%',
  time: '13%',
  bounce: '9%',
  clicks: '12%',
  // status: '8%',
  status: '13%',
};

/* ── Sort indicator ── */
/* ── Sort indicator ── */
const SortIcon = ({ field, current, order }) =>
  current === field
    ? order === 'asc'
      ? <FiChevronUp className="shrink-0 text-[#CC9900]" />
      : <FiChevronDown className="shrink-0 text-[#CC9900]" />
    : <span className="opacity-30 text-[10px] align-middle ml-0.5">↕</span>;

/* ── Status badge ── */
const StatusBadge = ({ status }) => {
  const styles = {
    'Top Performer': { bg: 'bg-amber-50 text-amber-800 border-amber-200/60' },
    'Trending': { bg: 'bg-violet-50 text-violet-800 border-violet-200/60' },
    'Active': { bg: 'bg-emerald-50 text-emerald-800 border-emerald-200/60' },
    'Low Activity': { bg: 'bg-orange-50 text-orange-800 border-orange-200/60' },
    'No Activity': { bg: 'bg-gray-50 text-gray-600 border-gray-200' },
    'High Activity': { bg: 'bg-sky-50 text-sky-800 border-sky-200/60' },
  };
  const labels = {
    'Top Performer': '⭐ Top', 'Trending': '🔥 Trending', 'Active': '✓ Active',
    'Low Activity': '⚠️ Low', 'No Activity': '💤 Idle', 'High Activity': '⚡ High',
  };
  const rawText = labels[status] || labels['Active'];
  const parts = rawText.split(' ');
  const emoji = parts[0];
  const label = parts.slice(1).join(' ');
  const config = styles[status] || styles['Active'];

  return (
    // <span className={`inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border h-6 whitespace-nowrap ${config.bg}`}>
    <span className={`inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border h-6 whitespace-nowrap shrink-0 ${config.bg}`}>
      <span className="inline-flex items-center text-[11px] leading-none">{emoji}</span>
      <span className="inline-flex items-center text-[11px] leading-none">{label}</span>
    </span>
  );
};

/* ── Rank badge ── */
const RankBadge = ({ rank }) => {
  const emojiStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, fontSize: 16, lineHeight: 1 };
  if (rank === 1) return <span style={emojiStyle}>🥇</span>;
  if (rank === 2) return <span style={emojiStyle}>🥈</span>;
  if (rank === 3) return <span style={emojiStyle}>🥉</span>;
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-50 text-gray-400 text-[10px] font-extrabold border border-gray-100/80 leading-none">
      #{rank}
    </span>
  );
};

/* ── Flex cell helper ── */
const Cell = ({ width, children, center = false, style = {} }) => (
  <div
    className={`shrink-0 flex items-center px-4 box-border ${center ? 'justify-center' : 'justify-start'}`}
    style={{ width, ...style }}
  >
    {children}
  </div>
);

/* ── Header cell ── */
const HeadCell = ({ width, children, center = false, sortable = false, onClick }) => (
  <div
    onClick={sortable ? onClick : undefined}
    className={`shrink-0 flex items-center gap-1 px-4 py-3 text.5 font-bold text-gray-400 text-[11px] uppercase tracking-wider select-none whitespace-nowrap box-border ${sortable ? 'cursor-pointer hover:text-[#CC9900]' : 'cursor-default'
      } ${center ? 'justify-center' : 'justify-start'}`}
    style={{ width }}
  >
    {children}
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   VenueAnalyticsTable
══════════════════════════════════════════════════════════════════ */
const VenueAnalyticsTable = ({
  venueFilter, setVenueFilter,
  venueSearch, setVenueSearch,
  sortField, setSortField,
  sortOrder, setSortOrder,
  paginatedVenues, filteredVenues,
  currentPage, setCurrentPage,
  totalPages, itemsPerPage,
  totalVenuesCount,
  isTableLoading,
}) => {
  console.log(paginatedVenues, "paginatedVenues")
  const [activeVenue, setActiveVenue] = useState(null); // venue object whose panel is open

  const handleSort = (field) => {
    if (sortField === field) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortOrder('desc'); }
  };

  const maxUsers = Math.max(...filteredVenues.map((v) => v.totalUsers), 1);
  const maxTime = Math.max(...filteredVenues.map((v) => v.totalAppTime || 0), 1);
  const ROW_H = 72;
  const totalCount = totalVenuesCount !== undefined ? totalVenuesCount : filteredVenues.length;

  return (
    <>



      <div className="bg-white border border-gray-100 rounded-[24px] shadow-[0_4px_20px_-2px_rgba(0,0,0,0.02)] overflow-hidden">

        {/* ── Header bar ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 border-b border-gray-100">
          <div>
            <p className="text-[11px] font-bold text-[#CC9900] uppercase tracking-wider mb-0.5">Venue Performance</p>
            <h2 className="text-lg font-extrabold text-[#111827] flex items-center gap-2 m-0">
              <MdOutlineStorefront className="text-[#CC9900]" /> Venue Analytics
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              {totalCount} venue{totalCount !== 1 ? 's' : ''} · click column headers to sort · click a row to see visitors
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex items-center">
              <FiSearch className="absolute left-3 text-gray-400 text-sm pointer-events-none" />
              <input
                type="text"
                placeholder="Search venues…"
                value={venueSearch}
                onChange={(e) => { setVenueSearch(e.target.value); setCurrentPage(1); }}
                className="pl-9 pr-4 py-2 text-xs bg-gray-50 border border-gray-200/80 rounded-xl w-60 text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-[#CC9900] focus:ring-2 focus:ring-[#CC9900]/10 transition-all duration-200"
              />
            </div>
          </div>
        </div>

        <div className="overflow-auto">
          <div className="max-md:w-[760px]">

            {/* ── Column headers ── */}
            <div className="flex items-center bg-gray-50/50 border-b border-gray-100">
              <HeadCell width={COL.venue}>Venue</HeadCell>
              <HeadCell width={COL.visitors}>
                <FiUsers style={{ fontSize: 11, marginRight: 4 }} />Visitors
              </HeadCell>
              <HeadCell width={COL.users} sortable onClick={() => handleSort('totalUsers')}>
                Count <SortIcon field="totalUsers" current={sortField} order={sortOrder} />
              </HeadCell>
              <HeadCell width={COL.time} sortable onClick={() => handleSort('totalAppTime')}>
                Time Spent <SortIcon field="totalAppTime" current={sortField} order={sortOrder} />
              </HeadCell>
              <HeadCell width={COL.bounce}>Bounce</HeadCell>
              <HeadCell width={COL.clicks} sortable onClick={() => handleSort('totalClicks')}>
                Clicks <SortIcon field="totalClicks" current={sortField} order={sortOrder} />
              </HeadCell>
              <HeadCell width={COL.status} center>Status</HeadCell>
            </div>

            {/* ── Data rows ── */}
            <div>
              {isTableLoading ? (
                Array.from({ length: itemsPerPage }).map((_, idx) => (
                  <div
                    key={idx}
                    className="flex items-center"
                    style={{ minHeight: ROW_H, borderBottom: '1px solid #F8FAFC' }}
                  >
                    <Cell width={COL.venue}>
                      <div className="flex items-center gap-3 w-full">
                        <div className="w-9 h-9 rounded-xl skeleton shrink-0" />
                        <div className="space-y-1.5 w-full">
                          <div className="h-3 w-2/3 skeleton" />
                          <div className="h-2.5 w-1/3 skeleton" />
                        </div>
                      </div>
                    </Cell>
                    <Cell width={COL.visitors}>
                      <div className="h-4 w-3/4 skeleton" />
                    </Cell>
                    <Cell width={COL.users}>
                      <div className="space-y-1.5 w-full">
                        <div className="h-3 w-1/3 skeleton" />
                        <div className="h-1.5 w-2/3 skeleton" />
                      </div>
                    </Cell>
                    <Cell width={COL.time}>
                      <div className="space-y-1.5 w-full">
                        <div className="h-3 w-1/3 skeleton" />
                        <div className="h-1.5 w-2/3 skeleton" />
                      </div>
                    </Cell>
                    <Cell width={COL.bounce}>
                      <div className="h-3 w-1/2 skeleton" />
                    </Cell>
                    <Cell width={COL.clicks}>
                      <div className="h-3 w-1/2 skeleton" />
                    </Cell>
                    <Cell width={COL.status} center>
                      <div className="h-5.5 w-16 rounded-full skeleton" />
                    </Cell>
                  </div>
                ))
              ) : paginatedVenues.length > 0 ? (
                paginatedVenues.map((venue, idx) => {
                  const globalRank = (currentPage - 1) * itemsPerPage + idx + 1;
                  const userBarW = Math.round((venue.totalUsers / maxUsers) * 100);
                  const timeBarW = Math.round(((venue.totalAppTime || 0) / maxTime) * 100);
                  const isLast = idx === paginatedVenues.length - 1;
                  const hasVisitors = venue.users && venue.users.length > 0;

                  return (
                    <div
                      key={venue.id}
                      onClick={() => setActiveVenue(venue)}
                      style={{
                        display: 'flex', alignItems: 'center',
                        minHeight: ROW_H,
                        borderBottom: isLast ? 'none' : '1px solid #F8FAFC',
                        background: '#FFF',
                        transition: 'background .15s, box-shadow .15s',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#FFFDF5';
                        e.currentTarget.style.boxShadow = 'inset 3px 0 0 #CC9900';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#FFF';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {/* Venue info */}
                      <Cell width={COL.venue}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            {venue.logo ? (
                              <img
                                src={methodModel.userImg(venue.logo)}
                                alt={venue.name}
                                style={{ width: 38, height: 38, borderRadius: 11, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.08)', display: 'block' }}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                              />
                            ) : null}
                            <div style={{
                              width: 38, height: 38, borderRadius: 11,
                              background: 'linear-gradient(135deg,rgba(204,153,0,.15),rgba(204,153,0,.05))',
                              border: '1px solid rgba(204,153,0,.2)',
                              display: venue.logo ? 'none' : 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              color: '#CC9900', fontWeight: 800, fontSize: 15,
                            }}>
                              {venue.name.charAt(0)}
                            </div>
                            {/* Rank badge overlaid */}
                            <div style={{ position: 'absolute', bottom: -6, right: -8, transform: 'scale(0.75)' }}>
                              <RankBadge rank={globalRank} />
                            </div>
                          </div>
                          <div>
                            <p style={{ fontWeight: 700, color: '#111827', fontSize: 13, margin: 0, lineHeight: '1.3', textTransform: 'capitalize' }}>{venue.name}</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#CC990012] text-[#CC9900] capitalize">
                              {venue?.category || '—'}
                            </span>
                          </div>
                        </div>
                      </Cell>

                      {/* Visitors — stacked avatars + count */}
                      <Cell width={COL.visitors}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {hasVisitors ? (
                            <>
                              {/* Avatar row */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <StackedAvatarGroup
                                  users={venue.users}
                                  totalCount={venue.totalUsers}
                                  size={26}
                                  maxShow={4}
                                />
                              </div>
                              {/* Count + View button row */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 700, color: '#374151',
                                  background: 'rgba(204,153,0,0.08)',
                                  border: '1px solid rgba(204,153,0,0.2)',
                                  borderRadius: 999, padding: '1px 7px',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {venue.totalUsers.toLocaleString()} users
                                </span>
                                <button
                                  onClick={e => { e.stopPropagation(); setActiveVenue(venue); }}
                                  className="text-[10px] font-bold text-[#CC9900] bg-[#CC9900]/10 hover:bg-[#CC9900] hover:text-white border border-[#CC9900]/20 hover:border-[#CC9900] px-2.5 py-1 rounded-full uppercase tracking-wider transition-all duration-200 cursor-pointer"
                                >
                                  View
                                </button>
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{
                                width: 26, height: 26, borderRadius: '50%',
                                background: '#F3F4F6', border: '2px solid #E5E7EB',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <FiUsers style={{ color: '#D1D5DB', fontSize: 10 }} />
                              </div>
                              <span style={{ fontSize: 10, color: '#D1D5DB' }}>
                                {venue.totalUsers > 0 ? venue.totalUsers.toLocaleString() : '—'}
                              </span>
                            </div>
                          )}
                        </div>
                      </Cell>

                      {/* Users count */}
                      <Cell width={COL.users}>
                        <div>
                          <p style={{ fontWeight: 800, color: '#111827', fontSize: 13, margin: 0 }}>{venue.totalUsers.toLocaleString()}</p>
                          <div className="flex items-center gap-6 mt-1.5">
                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div style={{ width: `${userBarW}%` }} className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />
                            </div>
                          </div>
                        </div>
                      </Cell>

                      {/* Time */}
                      <Cell width={COL.time}>
                        <div>
                          <p style={{ fontWeight: 700, color: '#111827', fontSize: 13, margin: 0 }}>
                            {venue.timeSpentHours} <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 11 }}>hrs</span>
                          </p>
                          <div className="flex items-center gap-6 mt-1.5">
                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div style={{ width: `${timeBarW}%` }} className="h-full bg-gradient-to-r from-[#CC9900] to-[#F0C040] rounded-full" />
                            </div>
                          </div>
                        </div>
                      </Cell>

                      {/* Bounce */}
                      <Cell width={COL.bounce}>
                        <div>
                          <p style={{ fontWeight: 600, color: '#374151', fontSize: 13, margin: 0 }}>{venue.bounceRate}</p>
                          <p style={{ fontSize: 10, color: '#9CA3AF', margin: 0, marginTop: 2 }}>of visits</p>
                        </div>
                      </Cell>

                      {/* Clicks */}
                      <Cell width={COL.clicks}>
                        <div>
                          <p style={{ fontWeight: 700, color: '#111827', fontSize: 13, margin: 0 }}>{venue.totalClicks.toLocaleString()}</p>
                          <p style={{ fontSize: 10, color: '#9CA3AF', margin: 0, marginTop: 2 }}>recorded</p>
                        </div>
                      </Cell>

                      {/* Status */}
                      <Cell width={COL.status} center>
                        <StatusBadge status={venue.status} />
                      </Cell>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 16, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiSearch style={{ fontSize: 22, color: '#D1D5DB' }} />
                  </div>
                  <p style={{ fontWeight: 600, color: '#374151', fontSize: 14, margin: 0 }}>No venues found</p>
                  <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>Try adjusting your search or filter.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4.5 border-t border-gray-100 flex-wrap gap-3">
            <span className="text-xs text-gray-500 font-medium">
              Showing{' '}
              <strong className="text-gray-900 font-bold">
                {totalCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}–{Math.min(totalCount, currentPage * itemsPerPage)}
              </strong>
              {' '}of{' '}
              <strong className="text-gray-900 font-bold">{totalCount}</strong> venues
            </span>

            <div className="flex items-center gap-1.5">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-3.5 py-1.5 text-xs font-semibold rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed transition-all duration-150"
              >
                ← Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pn) => (
                <button
                  key={pn}
                  onClick={() => setCurrentPage(pn)}
                  className={`w-8 h-8 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer flex items-center justify-center ${currentPage === pn
                      ? 'bg-[#CC9900] text-white shadow-md shadow-[#CC9900]/20'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                >
                  {pn}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="px-3.5 py-1.5 text-xs font-semibold rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed transition-all duration-150"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Visitors Panel Modal ── */}
      {activeVenue && (
        <VenueVisitorsPanel
          venue={activeVenue}
          onClose={() => setActiveVenue(null)}
        />
      )}
    </>
  );
};

export default React.memo(VenueAnalyticsTable);
